import { IconEdit, IconLoader, IconPlus, IconX } from '@tabler/icons-react';
import React, { useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import { useForm } from 'react-hook-form';
import { useEmployeeAPI } from 'src/API/rest.employee';
import {
    CalenderActinPostType,
    useForecastCalenderAPI,
    useGetLatestCalendarDate,
} from 'src/API/rest.forecast_calender';
import { ButtonIconSquare } from 'src/components/buttons';
import {
    ModalStandard,
    ModalStandardContent,
    ModalStandardDescription,
    ModalStandardFooter,
    ModalStandardHeader,
    ModalStandardTitle,
} from 'src/components/standard.modal';
import { MultiselectStandardOnline } from 'src/components/standard.multiselectOnline';
import { cn } from 'src/lib/utils';
import { toast } from 'src/shadcn/ui/use-toast';
import 'react-datepicker/dist/react-datepicker.css';
import { format } from 'date-fns';

interface Props {
    show?: boolean;
    setShow?: (show: boolean) => void;
    refetchforcastCalendersList: () => void;
    selectedDate: string;
}

/* ---------- API Response Types (Strict, No React Components) ---------- */
interface ApiCalendarLine {
    ForecastCalenderLineID: number | null;
    Status?: number | string;
    Date?: string;
    TargetDate?: string;
    MailToEnable?: boolean;
    FT07_is_completed?: boolean;
    FT07_completed_date?: string | null;
    Responsibility?: {
        ForecastCalenderResponsibilityID: number | null;
        ForecastCalenderline: number | null;
        EmployeeID: number;
        FT08_is_completed?: boolean;
        FT08_completed_date?: string | null;
    }[];
    MailTo?: {
        MailToEmployeeID: number;
        MailToID: number;
        FT08_is_completed?: boolean;
        FT08_completed_date?: string | null;
    }[];
}

/* ---------- Internal Form Types ---------- */
interface Responsibility {
    ForecastCalenderResponsibilityID: number | null;
    ForecastCalenderline: number | null;
    FM03_EmployeeID: number;
    FT08_is_completed?: boolean;
    FT08_completed_date?: string | null;
}

interface MailTo {
    FT08_email_to: number;
    FT08_ForecastCalenderResponsibilityID?: number;
    FT08_is_completed?: boolean;
    FT08_completed_date?: string | null;
}

interface OptionsType {
    ForecastCalenderLineID?: number | null;
    SINo: number;
    FT06_forecastCalenderMasterID: number;
    Action: string;
    FT07_TargetDate?: string;
    FT07_Status: number;
    FT07_Date: string;
    Responsibility: Responsibility[];
    MailTo?: MailTo[];
    FT07_email_to_enable?: boolean;
    FT07_is_completed?: boolean;
    FT07_completed_date?: string | null;
}

interface OptionsTypeForm {
    FT07_completed_date: any;
    FT07_is_completed: boolean;
    SINo: number;
    FT06_forecastCalenderMasterID: number;
    Action: string;
    FT07_TargetDate?: string;
    FT07_Status: number;
    FT07_Date: string;
    Responsibility: {
        FT08_completed_date: string | null;
        FT08_is_completed: boolean;
        FM03_EmployeeID: number;
        FT08_ForecastCalenderResponsibilityID?: number;
    }[];
    MailTo?: {
        FT08_email_to: number;
        FT08_ForecastCalenderResponsibilityID?: number;
        FT08_is_completed?: boolean;
        FT08_completed_date?: string | null;
    }[];
    FT07_email_to_enable?: boolean;
}

interface EmployeeOption {
    label: string;
    value: string;
}

/* ------------------------------------------------------------------- */
export function ModalAddForecastModal({
    show,
    setShow,
    refetchforcastCalendersList,
    selectedDate,
}: Props) {
    const {
        register,
        handleSubmit,
        setValue,
        reset,
        formState: { errors },
        watch,
    } = useForm<{ options: OptionsTypeForm[] }>({
        defaultValues: { options: [] },
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const [options, setOptions] = useState<OptionsType[]>([]);
    const [mode, setMode] = useState<'add' | 'edit'>('add');
    const [searchOldStyles, setSearchOldStyles] = useState('');
    const [unavailableEmployee, setUnavailableEmployee] = useState<EmployeeOption[]>([]);
    const [selectedItems, setSelectedItems] = useState<EmployeeOption[]>([]);

    const {
        isLoading: isLoadingForecastCalender,
        isError: isErrorForecastCalender,
        forcastCalendersList,
        isFetching: isFetchingForecastCalender,
        isFetched: isFetchedForcastCalender,
        createCalendarLine,
        updateCalendarLine,
    } = useForecastCalenderAPI({
        date: `${selectedDate}-01`,
        trigger: show,
    });

    const {
        latestCalendarDate,
        isLoadinglatestCalendarDate,
    } = useGetLatestCalendarDate({
        trigger: show && mode === 'add',
    });


    const {
        forcastCalendersList: latestForcastList,
        isFetching: isFetchingLatestForecast,
        isLoading: isLoadingLatestForecast,
    } = useForecastCalenderAPI({
        date: latestCalendarDate?.date,
        trigger: show && !!latestCalendarDate?.date && mode === 'add',
    });

    const {
        emloyees,
        isLoading: isLoadingEmployees,
        isFetching: isFetchingEmployees,
        isError: isErrorEmployees,
        readEmployeebyId,
    } = useEmployeeAPI({
        trigger: show,
        search: searchOldStyles,
    });

    const optionsSelected = watch('options');

    /* ---------- Load Offline Employees ---------- */
    useEffect(() => {
        const loadUnavailableEmployees = async (ids: { emlpyeeId: number }[]) => {
            const results = await Promise.all(
                ids.map(async ({ emlpyeeId }) => {
                    if (!emlpyeeId) return null;
                    try {
                        const resp = await readEmployeebyId({ employeeId: emlpyeeId });
                        if (resp?.success && resp.data) {
                            return {
                                label: `(${resp.data.FM03_EmployeeCode ?? ''})-${resp.data.FM03_EmployeeName ?? ''}`,
                                value: String(resp.data.FM03_EmployeeID),
                            };
                        }
                    } catch (e) {
                        console.warn('Failed to fetch employee', emlpyeeId, e);
                    }
                    return null;
                })
            );
            const filtered = results.filter((x): x is EmployeeOption => x !== null);
            if (filtered.length) setUnavailableEmployee((p) => [...p, ...filtered]);
        };

        if (optionsSelected?.length && show) {
            const ids = optionsSelected
                .flatMap((o) => o.Responsibility.map((r) => r.FM03_EmployeeID))
                .map((id) => ({ emlpyeeId: id }));
            loadUnavailableEmployees(ids);
        }

        if (!show) {
            setSelectedItems([]);
            setHasSubmitted(false);
            setUnavailableEmployee([]);
        }
    }, [optionsSelected?.length, show, readEmployeebyId]);

    /* ---------- Build Options from API ---------- */
    useEffect(() => {
        if (
            !isErrorForecastCalender &&
            isFetchedForcastCalender &&
            !isErrorEmployees &&
            forcastCalendersList
        ) {
            // Check if we are in 'add' mode effectively (lines are empty)
            const hasExistingLines = forcastCalendersList.some((d) => d.CalenderLines?.length);
            const isAddMode = !hasExistingLines;

            // Only rebuild options if we have what we need
            // If Add Mode, we wait for latestForcastList if we expect it?
            // User requirement: "using latest date data".
            // If latestForcastList is loading, we might want to wait?
            // But we shouldn't block if there IS no latest data.

            const built: OptionsType[] = forcastCalendersList.map((item) => {
                const line = item.CalenderLines?.[0] as unknown as ApiCalendarLine | undefined;

                // TF: Try to find latest data if (isAddMode)
                let latestMatch: ApiCalendarLine | undefined;
                if (isAddMode && latestForcastList) {
                    const matchItem = latestForcastList.find(l => l.ForecastCalenderMasterID === item.ForecastCalenderMasterID);
                    latestMatch = matchItem?.CalenderLines?.[0] as unknown as ApiCalendarLine | undefined;
                }

                return {
                    ForecastCalenderLineID: line?.ForecastCalenderLineID ?? null,
                    SINo: Number(item.SINo),
                    FT06_forecastCalenderMasterID: item.ForecastCalenderMasterID,
                    Action: item.Action,
                    FT07_TargetDate: line?.TargetDate ?? undefined, // Keep target date empty for new? Or undefined.
                    FT07_Status: Number(line?.Status ?? 1),
                    FT07_Date: line?.Date ?? '',
                    FT07_email_to_enable: line?.MailToEnable ?? latestMatch?.MailToEnable ?? false,
                    FT07_is_completed: line?.FT07_is_completed ?? false,
                    FT07_completed_date: line?.FT07_completed_date ?? null,
                    Responsibility:
                        (line?.Responsibility ?? latestMatch?.Responsibility)?.map((emp) => ({
                            ForecastCalenderResponsibilityID: line ? emp.ForecastCalenderResponsibilityID : null, // If copying from latest, new ID is null
                            ForecastCalenderline: line ? emp.ForecastCalenderline : null,
                            FM03_EmployeeID: emp.EmployeeID,
                            FT08_is_completed: line ? (emp.FT08_is_completed ?? false) : false,
                            FT08_completed_date: line ? (emp.FT08_completed_date ?? null) : null,
                        })) ?? [],
                    MailTo:
                        (line?.MailTo ?? latestMatch?.MailTo)?.map((mail) => ({
                            FT08_email_to: mail.MailToEmployeeID,
                            FT08_ForecastCalenderResponsibilityID: line ? mail.MailToID : undefined,
                            FT08_is_completed: line ? (mail.FT08_is_completed ?? false) : false,
                            FT08_completed_date: line ? (mail.FT08_completed_date ?? null) : null,
                        })) ?? [],
                };
            });

            reset({ options: built });
            setOptions(built);
            setMode(hasExistingLines ? 'edit' : 'add');
        }

        if (isErrorEmployees || isErrorForecastCalender) {
            toast({ description: 'Error fetching data', title: 'Error' });
        }
    }, [
        isErrorForecastCalender,
        isFetchedForcastCalender,
        isErrorEmployees,
        forcastCalendersList,
        latestForcastList, // Added dependency
        reset,
        show,
        mode, // Added dependency to re-run if mode switches (though mode is derived)
        isFetchingForecastCalender,
    ]);

    /* ---------- Submit Handler ---------- */
    const onSubmit = async (data: { options: OptionsTypeForm[] }, e?: React.BaseSyntheticEvent) => {
        if (isSubmitting || hasSubmitted) {
            e?.preventDefault();
            return;
        }
        setIsSubmitting(true);
        setHasSubmitted(true);

        const formattedDate = new Date(selectedDate).toISOString().split('T')[0];
        const calenderLines: any[] = [];

        data.options.forEach((opt, idx) => {
            const original = options[idx];

            const lineCompleted = original?.Responsibility?.some((r) => r.FT08_is_completed) ?? false;
            const lineCompletedDate =
                original?.Responsibility?.find((r) => r.FT08_completed_date)?.FT08_completed_date ?? null;

            const responsibilityPayload = opt.Responsibility.map((r) => {
                const orig = original?.Responsibility?.find((o) => o.FM03_EmployeeID === r.FM03_EmployeeID);
                return {
                    FM03_EmployeeID: r.FM03_EmployeeID,
                    FT08_is_completed: orig?.FT08_is_completed ?? false,
                    FT08_completed_date: orig?.FT08_completed_date ?? null,
                    FT08_Status: 1,
                    ...(orig?.ForecastCalenderResponsibilityID && {
                        FT08_ForecastCalenderResponsibilityID: orig.ForecastCalenderResponsibilityID,
                    }),
                };
            });

            const mailToPayload = (opt.MailTo ?? []).map((mail) => {
                const orig = original?.MailTo?.find((m) => m.FT08_email_to === mail.FT08_email_to);
                return {
                    FT08_email_to: mail.FT08_email_to,
                    FT08_is_completed: orig?.FT08_is_completed ?? false,
                    FT08_completed_date: orig?.FT08_completed_date ?? null,
                    FT08_Status: 1,
                    ...(orig?.FT08_ForecastCalenderResponsibilityID && {
                        FT08_ForecastCalenderResponsibilityID: orig.FT08_ForecastCalenderResponsibilityID,
                    }),
                };
            });

            calenderLines.push({
                FT07_ForecastCalenderLineID: original?.ForecastCalenderLineID ?? null,
                FT06_forecastCalenderMasterID: opt.FT06_forecastCalenderMasterID,
                FT07_Date: formattedDate,
                FT07_TargetDate: opt.FT07_TargetDate ?? null,
                FT07_Status: opt.FT07_Status,
                FT07_email_to_enable: opt.FT07_email_to_enable ?? false,
                FT07_is_completed: opt.FT07_is_completed ?? lineCompleted,
                FT07_completed_date: opt.FT07_completed_date ?? lineCompletedDate,
                Responsibility: responsibilityPayload,
                MailTo: mailToPayload,
            });
        });

        const finalPayload = {
            date: formattedDate,
            calenderLines,
            deletedResponsibility: [] as any[],
        };

        console.log('FINAL PAYLOAD:', JSON.stringify(finalPayload, null, 2));

        let response;
        if (mode === 'add') {
            response = await createCalendarLine({
                date: `${selectedDate}-01`,
                actionsLines: data.options.map((opt) => ({
                    SINo: opt.SINo,
                    Action: opt.Action,
                    FT06_forecastCalenderMasterID: opt.FT06_forecastCalenderMasterID,
                    FT07_TargetDate: opt.FT07_TargetDate ?? null,
                    FT07_Date: formattedDate,
                    FT07_email_to_enable: opt.FT07_email_to_enable ?? false,
                    FT07_is_completed: false,
                    FT07_completed_date: null,
                    FT07_Status: 1,
                    Responsibility: opt.Responsibility.map((r) => ({
                        FM03_EmployeeID: r.FM03_EmployeeID,
                        FT08_is_completed: false,
                        FT08_completed_date: null,
                        FT08_Status: 1,
                    })),
                    MailTo: (opt.MailTo ?? []).map((m) => ({
                        FT08_email_to: m.FT08_email_to,
                        FT08_is_completed: false,
                        FT08_completed_date: null,
                        FT08_Status: 1,
                    })),
                })),
            });
        } else {
            response = await updateCalendarLine({
                data: finalPayload,
                date: formattedDate,
            });
        }

        if (response.success) {
            toast({ description: response.message });
            setShow?.(false);
            refetchforcastCalendersList();
        } else {
            toast({ description: response.message, title: 'Error' });
        }

        setIsSubmitting(false);
    };

    const isAnythingLoading =
        isLoadingForecastCalender ||
        isFetchingForecastCalender ||
        isFetchingEmployees ||
        isLoadingEmployees ||
        (mode === 'add' && isLoadinglatestCalendarDate) ||
        (mode === 'add' && isLoadingLatestForecast);
    const isAnyError = isErrorForecastCalender || isErrorEmployees;

    const realEmployees: EmployeeOption[] = [
        ...(emloyees
            ?.filter((e) => e.FM03_EmployeeID != null)
            .map((e) => ({
                value: String(e.FM03_EmployeeID),
                label: `(${e.FM03_EmployeeCode ?? ''})-${e.FM03_EmployeeName ?? ''}`,
            })) ?? []),
        ...unavailableEmployee,
        ...selectedItems,
    ];
    const realEmployeesUnique = Array.from(new Set(realEmployees.map((s) => s.value))).map(
        (v) => realEmployees.find((s) => s.value === v)!
    );

    return (
        <ModalStandard open={show} onOpenChange={setShow}>
            <ModalStandardContent className="bg-gray-100 !max-w-[1000px]">
                <form onSubmit={handleSubmit(onSubmit)} className="overflow-auto">
                    <ModalStandardHeader>
                        <ModalStandardTitle>
                            {mode === 'edit' ? 'Edit Forecast Calendar' : 'Add Forecast Calendar'}
                        </ModalStandardTitle>

                        <ModalStandardDescription className="overflow-auto">
                            {isAnythingLoading && (
                                <div className="px-2 p-1 rounded-md bg-vstargray-800/30 whitespace-nowrap w-min absolute left-1/2 -translate-x-1/2">
                                    Updating...
                                </div>
                            )}

                            <div className="overflow-x-auto">
                                <div className="inline-block min-w-full align-middle">
                                    <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                                        <table className="min-w-full">
                                            <thead>
                                                <tr>
                                                    <th />
                                                    <th />
                                                    <th />
                                                    <th />
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white">
                                                {options.map((item, index) => {
                                                    const responsibilityIds = watch(`options.${index}.Responsibility`)?.map(
                                                        (r) => String(r.FM03_EmployeeID)
                                                    ) ?? [];
                                                    const targetDate = watch(`options.${index}.FT07_TargetDate`);
                                                    const mailToEntries = watch(`options.${index}.MailTo`) ?? [];
                                                    const emailEnabled = watch(`options.${index}.FT07_email_to_enable`) ?? false;

                                                    return (
                                                        <React.Fragment key={index}>
                                                            <tr>
                                                                <td
                                                                    colSpan={2}
                                                                    className="px-6 py-4 font-bold text-red-900 whitespace-nowrap text-xl"
                                                                >
                                                                    {item.SINo} - {item.Action}
                                                                </td>
                                                            </tr>

                                                            <tr>
                                                                <td className="px-6 py-2 whitespace-nowrap">
                                                                    <label className="block text-sm font-medium mb-1">
                                                                        Target Date
                                                                    </label>
                                                                    <DatePicker
                                                                        id={`datepicker-${index}`}
                                                                        selected={targetDate ? new Date(targetDate) : null}
                                                                        onChange={(date: Date | null) => {
                                                                            setValue(
                                                                                `options.${index}.FT07_TargetDate`,
                                                                                date ? format(date, 'yyyy-MM-dd') : ''
                                                                            );
                                                                        }}
                                                                        dateFormat="dd-MMM-yyyy"
                                                                        customInput={
                                                                            <input
                                                                                type="text"
                                                                                value={targetDate ? format(new Date(targetDate), 'dd-MMM-yyyy') : ''}
                                                                                disabled
                                                                                style={{
                                                                                    padding: '5px',
                                                                                    border: '1px solid #ccc',
                                                                                    borderRadius: '4px',
                                                                                }}
                                                                            />
                                                                        }
                                                                    />
                                                                </td>

                                                                <td className="px-6 whitespace-nowrap">
                                                                    <label className="block text-sm font-medium mb-1">
                                                                        Responsibility
                                                                    </label>
                                                                    <MultiselectStandardOnline
                                                                        {...register(`options.${index}.Responsibility`, {
                                                                            validate: (v) =>
                                                                                targetDate && !v?.length ? 'Responsibility is required' : undefined,
                                                                        })}
                                                                        options={realEmployeesUnique}
                                                                        error={errors.options?.[index]?.Responsibility?.message}
                                                                        value={responsibilityIds}
                                                                        onValueChange={(val) => {
                                                                            setSelectedItems((p) => [
                                                                                ...p,
                                                                                ...val.target.item.map((i) => ({
                                                                                    label: i.label,
                                                                                    value: i.value,
                                                                                })),
                                                                            ]);
                                                                            setValue(
                                                                                `options.${index}.Responsibility`,
                                                                                val.target.item.map((i) => ({
                                                                                    FM03_EmployeeID: Number(i.value),
                                                                                    FT08_is_completed: false,
                                                                                    FT08_completed_date: null,
                                                                                }))
                                                                            );
                                                                        }}
                                                                        onChangeSearch={setSearchOldStyles}
                                                                        disabled={isAnythingLoading}
                                                                    />
                                                                </td>
                                                            </tr>

                                                            <tr
                                                                className={cn([
                                                                    index === options.length - 1 ? '' : 'border-b-8 border-[#f3f4f6]',
                                                                    'bg-white',
                                                                ])}
                                                            >
                                                                <td className="px-6 py-2 whitespace-nowrap">
                                                                    <label className="block text-sm font-medium mb-1">
                                                                        Intimation mail
                                                                    </label>
                                                                    <MultiselectStandardOnline
                                                                        value={mailToEntries.map((m) => String(m.FT08_email_to))}
                                                                        {...register(`options.${index}.MailTo`)}
                                                                        options={realEmployeesUnique}
                                                                        onValueChange={(val) => {
                                                                            setSelectedItems((p) => [
                                                                                ...p,
                                                                                ...val.target.item.map((i) => ({
                                                                                    label: i.label,
                                                                                    value: i.value,
                                                                                })),
                                                                            ]);
                                                                            setValue(
                                                                                `options.${index}.MailTo`,
                                                                                val.target.item.length
                                                                                    ? val.target.item.map((itm) => {
                                                                                        const existing = mailToEntries.find(
                                                                                            (m) => String(m.FT08_email_to) === itm.value
                                                                                        );
                                                                                        return {
                                                                                            FT08_email_to: Number(itm.value),
                                                                                            FT08_ForecastCalenderResponsibilityID:
                                                                                                existing?.FT08_ForecastCalenderResponsibilityID,
                                                                                            FT08_is_completed: false,
                                                                                            FT08_completed_date: null,
                                                                                        };
                                                                                    })
                                                                                    : []
                                                                            );
                                                                        }}
                                                                        onChangeSearch={setSearchOldStyles}
                                                                        disabled={isAnythingLoading}
                                                                        name={`options.${index}.MailTo`}
                                                                    />
                                                                </td>

                                                                <td className="px-6 py-2 whitespace-nowrap align-top">
                                                                    <label className="flex items-center space-x-2">
                                                                        <input
                                                                            type="checkbox"
                                                                            className="w-4 h-4 accent-red-500 border-2 rounded focus:ring-red-500 focus:ring-2 transition-all duration-150"
                                                                            {...register(`options.${index}.FT07_email_to_enable`)}
                                                                            disabled={isAnythingLoading || mailToEntries.length === 0}
                                                                        />
                                                                        <span className="text-sm font-medium">Enable Intimation Mail</span>
                                                                    </label>
                                                                    {mailToEntries.length === 0 && (
                                                                        <p className="text-xs text-gray-500 mt-1">
                                                                            Select employees to enable mail
                                                                        </p>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </ModalStandardDescription>
                    </ModalStandardHeader>

                    <ModalStandardFooter>
                        <ButtonIconSquare
                            variant="white"
                            icon={<IconX />}
                            text="Close"
                            onClick={(e) => {
                                setShow?.(false);
                                e?.preventDefault();
                            }}
                            disabled={isSubmitting}
                        />
                        <ButtonIconSquare
                            variant="secondary"
                            icon={
                                isSubmitting ? (
                                    <IconLoader className="animate-spin" />
                                ) : mode === 'add' ? (
                                    <IconPlus />
                                ) : (
                                    <IconEdit />
                                )
                            }
                            text={mode === 'add' ? 'Add' : 'Edit'}
                            type="submit"
                            disabled={isSubmitting || isAnythingLoading || isAnyError || hasSubmitted}
                        />
                    </ModalStandardFooter>
                </form>
            </ModalStandardContent>
        </ModalStandard>
    );
}